/**
 * Page state machine — hand-rolled reducer pattern.
 *
 * Why not XState: ~7 states with deterministic transitions don't need XState's
 * actor model overhead. Discriminated unions + a switch covers it in 100 lines
 * and tests in another 100. If state grows to async parallel actors later, swap
 * in XState then.
 *
 * Shape (per design doc state diagram + Codex soft-gate revision):
 *
 *   flow:   ready → filling ⇄ invalid → calculating → results → generating-pdf
 *                                                                  ↓
 *                                                              pdf-error
 *
 *   modal:  closed | practice-setup-required | practice-editing
 *
 *   practice info hydrates from localStorage on init. The form is freely
 *   usable without practice info; the modal triggers ONLY at PDF download
 *   time IF practice info is empty (soft gate). After practice-setup save,
 *   the pending download resumes automatically.
 *
 * Reducer is a pure function: same (state, action) → same state. UI side-effects
 * (200ms loading minima, calling pdf-lib, focusing the first invalid field)
 * live OUTSIDE this module. The reducer just describes valid transitions.
 */

import { type CalcInput, type CalcResults, calculate } from "./calc";
import type { PracticeInfo } from "./practice-info";

// -----------------------------------------------------------------------------
// Form values + validation

/**
 * Raw string values from form inputs. The four numeric measurements drive the
 * calculation; `babyName` is optional metadata that flows through to the PDF
 * (substituted for `[Baby's Name]` placeholders) and is also reflected in the
 * on-screen variant paragraph. Empty babyName falls back to "Your baby".
 *
 * Reducer parses + validates the four numeric fields; babyName is never
 * validated (it's optional and free-form).
 */
export interface FormValues {
  babyName: string;
  apLength: string;
  rightDiagonal: string;
  leftDiagonal: string;
  parietalWidth: string;
}

export const EMPTY_FORM: FormValues = {
  babyName: "",
  apLength: "",
  rightDiagonal: "",
  leftDiagonal: "",
  parietalWidth: "",
};

export type FieldErrors = Partial<Record<keyof FormValues, string>>;

/**
 * Plausibility band for a single measurement, in millimetres. The form labels
 * every field "(mm)", so this is a unit the DC has already been told.
 *
 * This is a TYPO GUARD, not a clinical range. The bounds are deliberately far
 * outside any real infant skull in both directions so a legitimate measurement
 * — preterm, macrocephalic, older toddler — can never be rejected. What it
 * catches is the order-of-magnitude slip: a dropped digit (12 for 120), an
 * extra one (1400), or centimetres typed into a millimetre field (13.3).
 *
 * Why it matters: CI is (width / AP) and is unbounded above, so an AP mistyped
 * as 12 prints a CI of 975 onto a results card and a parent handout sized for
 * values like "78.0". CVAI cannot run away the same way — dividing by the
 * GREATER diagonal caps it below 100% — but a mistyped diagonal still yields an
 * asymmetry no skull has.
 *
 * Deliberately NOT enforced in calc.ts: that module is pure ratio math and is
 * documented (and tested) as unit-agnostic — CI and CVAI are identical whether
 * the caller passes mm or cm. Millimetre bounds are a property of this product
 * surface, not of the math.
 */
const MEASUREMENT_MIN_MM = 50;
const MEASUREMENT_MAX_MM = 250;

/** The four measurement fields, in form order. `babyName` is not a measurement. */
const MEASUREMENT_KEYS = [
  "apLength",
  "rightDiagonal",
  "leftDiagonal",
  "parietalWidth",
] as const satisfies ReadonlyArray<keyof FormValues>;

const ERR_POSITIVE = "Enter a positive number";
const ERR_RANGE = `Enter a measurement in mm (${MEASUREMENT_MIN_MM}–${MEASUREMENT_MAX_MM})`;
const ERR_PAIR = "Check this pair — together they aren't a possible head shape";
const ERR_SCALE = "Check this against AP length — these aren't the same skull";

/**
 * Derived-value guard. Per-field bounds cannot catch a set where every value is
 * individually in range but the combination is anatomically impossible: AP 50
 * with width 250 clears four independent checks and yields CI 500, printing an
 * urgent classification onto a parent handout.
 *
 * Same discipline as the per-field band — a typo guard, not a clinical range.
 * Real infant CI runs roughly 65-105 and real CVAI rarely passes 20, so these
 * sit multiples away from anything a skull can produce.
 */
const CI_MIN = 40;
const CI_MAX = 150;
const CVAI_MAX = 50;

/**
 * Cross-pair scale guard. Bounding CI and CVAI separately still lets the two
 * pairs disagree about how big the skull is: AP 250 with width 100 gives CI 40,
 * and diagonals 75/50 give CVAI 33, both inside their bounds, but no skull is
 * 250 mm front-to-back with 50 mm diagonals.
 *
 * A frontal-to-contralateral-occipital diagonal runs close to the AP length —
 * measured cases here sit between 0.72x and 1.19x. Bounding at 0.5x-2.0x leaves
 * wide margin around that while still rejecting a pair measured at a different
 * scale.
 */
const DIAGONAL_TO_AP_MIN = 0.5;
const DIAGONAL_TO_AP_MAX = 2.0;

/**
 * Parse a single field. Empty / non-numeric / non-positive returns null.
 * Caller decides whether `null` is "still typing" or "invalid for submit".
 */
function parseField(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/** True when a parsed measurement falls inside the millimetre typo guard. */
function isPlausibleMm(n: number): boolean {
  return n >= MEASUREMENT_MIN_MM && n <= MEASUREMENT_MAX_MM;
}

/**
 * Parse one measurement field and return either its value or the message to
 * show beneath it. Keeps `validate` from repeating a three-branch check per
 * field, so a future field can't accidentally skip the range guard.
 */
function parseMeasurement(raw: string): { value: number } | { error: string } {
  const n = parseField(raw);
  if (n === null) return { error: ERR_POSITIVE };
  if (!isPlausibleMm(n)) return { error: ERR_RANGE };
  return { value: n };
}

/**
 * Validate the whole form. If every field parses to a plausible measurement and
 * the set describes one skull, returns the typed CalcInput; otherwise returns
 * the field-level errors.
 */
function validate(
  values: FormValues,
): { ok: true; input: CalcInput } | { ok: false; errors: FieldErrors } {
  const errors: FieldErrors = {};
  const parsed = {
    apLength: parseMeasurement(values.apLength),
    rightDiagonal: parseMeasurement(values.rightDiagonal),
    leftDiagonal: parseMeasurement(values.leftDiagonal),
    parietalWidth: parseMeasurement(values.parietalWidth),
  } as const;

  for (const key of MEASUREMENT_KEYS) {
    const r = parsed[key];
    if ("error" in r) errors[key] = r.error;
  }

  // Pair checks run on whichever pairs fully parsed, so a bad field elsewhere
  // doesn't hide them. Reporting everything in one pass means the DC fixes the
  // form once instead of discovering a second error after correcting the first.
  const ap = "value" in parsed.apLength ? parsed.apLength.value : null;
  const width = "value" in parsed.parietalWidth ? parsed.parietalWidth.value : null;
  const right = "value" in parsed.rightDiagonal ? parsed.rightDiagonal.value : null;
  const left = "value" in parsed.leftDiagonal ? parsed.leftDiagonal.value : null;

  if (ap !== null && width !== null) {
    const ci = (width / ap) * 100;
    if (ci < CI_MIN || ci > CI_MAX) {
      errors.apLength = ERR_PAIR;
      errors.parietalWidth = ERR_PAIR;
    }
  }

  if (right !== null && left !== null) {
    // Mirrors computeCVAI: the denominator is the GREATER diagonal.
    const cvai = (Math.abs(right - left) / Math.max(right, left)) * 100;
    if (cvai > CVAI_MAX) {
      errors.rightDiagonal = ERR_PAIR;
      errors.leftDiagonal = ERR_PAIR;
    }
  }

  // Cross-pair: the diagonals and the AP length must describe one skull.
  if (ap !== null) {
    for (const [key, value] of [
      ["rightDiagonal", right],
      ["leftDiagonal", left],
    ] as const) {
      if (value === null) continue;
      const ratio = value / ap;
      if (ratio < DIAGONAL_TO_AP_MIN || ratio > DIAGONAL_TO_AP_MAX) {
        errors[key] = ERR_SCALE;
        errors.apLength ??= ERR_SCALE;
      }
    }
  }

  if (
    Object.keys(errors).length > 0 ||
    ap === null ||
    width === null ||
    right === null ||
    left === null
  ) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    input: { apLength: ap, rightDiagonal: right, leftDiagonal: left, parietalWidth: width },
  };
}

// -----------------------------------------------------------------------------
// State

export type Flow =
  | { tag: "ready" }
  | { tag: "filling"; values: FormValues }
  | { tag: "invalid"; values: FormValues; errors: FieldErrors }
  | { tag: "calculating"; values: FormValues; input: CalcInput }
  | { tag: "results"; values: FormValues; input: CalcInput; results: CalcResults }
  | {
      tag: "generating-pdf";
      values: FormValues;
      input: CalcInput;
      results: CalcResults;
    }
  | {
      tag: "pdf-error";
      values: FormValues;
      input: CalcInput;
      results: CalcResults;
      message: string;
    };

export type Modal =
  | { kind: "closed" }
  /** Soft gate: triggered when user clicks download with no practice info. */
  | { kind: "practice-setup-required" }
  /** User chose Edit Practice Info from the calc page footer. */
  | { kind: "practice-editing" };

export interface AppState {
  flow: Flow;
  modal: Modal;
  practice: PracticeInfo | null;
}

export function initialState(practice: PracticeInfo | null): AppState {
  return {
    flow: { tag: "ready" },
    modal: { kind: "closed" },
    practice,
  };
}

// -----------------------------------------------------------------------------
// Actions

export type Action =
  // Slight name lie: this action also carries babyName edits (which isn't a
  // measurement). The shape — generic field-of-FormValues + string value —
  // covers both cases identically. Renaming to `set-form-field` would be
  // ~50 test-case edits for no behavior change; leaving as-is.
  | { type: "set-measurement"; field: keyof FormValues; value: string }
  | { type: "calculate-requested" }
  | { type: "calculate-completed" }
  | { type: "download-requested" }
  | { type: "pdf-completed" }
  | { type: "pdf-failed"; message: string }
  | { type: "pdf-error-dismissed" }
  | { type: "open-edit-practice" }
  | { type: "practice-saved"; info: PracticeInfo }
  | { type: "practice-cancelled" }
  | { type: "reset-form" };

// -----------------------------------------------------------------------------
// Reducer

function getValues(flow: Flow): FormValues {
  return flow.tag === "ready" ? EMPTY_FORM : flow.values;
}

function withMeasurement(values: FormValues, field: keyof FormValues, value: string): FormValues {
  return { ...values, [field]: value };
}

export function reduce(state: AppState, action: Action): AppState {
  switch (action.type) {
    case "set-measurement": {
      // Any flow other than generating-pdf accepts edits; in pdf-generating we
      // ignore (UI shouldn't dispatch, but we're defensive). Editing AFTER
      // results invalidates them — typing a new value drops you back to "filling".
      if (state.flow.tag === "generating-pdf") return state;
      const values = withMeasurement(getValues(state.flow), action.field, action.value);
      return { ...state, flow: { tag: "filling", values } };
    }

    case "calculate-requested": {
      // Only meaningful from filling / invalid / results. Anywhere else: no-op.
      if (
        state.flow.tag !== "filling" &&
        state.flow.tag !== "invalid" &&
        state.flow.tag !== "results"
      ) {
        return state;
      }
      const values = getValues(state.flow);
      const result = validate(values);
      if (!result.ok) {
        return { ...state, flow: { tag: "invalid", values, errors: result.errors } };
      }
      return { ...state, flow: { tag: "calculating", values, input: result.input } };
    }

    case "calculate-completed": {
      if (state.flow.tag !== "calculating") return state;
      // Run the math here so the UI's setTimeout(200ms) doesn't have to know
      // the input shape. Could be hoisted out, but keeping it co-located keeps
      // the action surface small.
      const results = calculate(state.flow.input);
      return {
        ...state,
        flow: {
          tag: "results",
          values: state.flow.values,
          input: state.flow.input,
          results,
        },
      };
    }

    case "download-requested": {
      if (state.flow.tag !== "results") return state;
      // Soft gate: no practice info → modal first. The flow stays in `results`
      // so practice-saved can resume from there.
      if (state.practice === null) {
        return { ...state, modal: { kind: "practice-setup-required" } };
      }
      return {
        ...state,
        flow: {
          tag: "generating-pdf",
          values: state.flow.values,
          input: state.flow.input,
          results: state.flow.results,
        },
      };
    }

    case "pdf-completed": {
      if (state.flow.tag !== "generating-pdf") return state;
      return {
        ...state,
        flow: {
          tag: "results",
          values: state.flow.values,
          input: state.flow.input,
          results: state.flow.results,
        },
      };
    }

    case "pdf-failed": {
      if (state.flow.tag !== "generating-pdf") return state;
      return {
        ...state,
        flow: {
          tag: "pdf-error",
          values: state.flow.values,
          input: state.flow.input,
          results: state.flow.results,
          message: action.message,
        },
      };
    }

    case "pdf-error-dismissed": {
      if (state.flow.tag !== "pdf-error") return state;
      return {
        ...state,
        flow: {
          tag: "results",
          values: state.flow.values,
          input: state.flow.input,
          results: state.flow.results,
        },
      };
    }

    case "open-edit-practice": {
      // Allowed from anywhere except mid-PDF generation (don't yank state from under it).
      if (state.flow.tag === "generating-pdf") return state;
      return { ...state, modal: { kind: "practice-editing" } };
    }

    case "practice-saved": {
      // Closing the modal always saves practice and clears it. Soft-gate variant
      // additionally resumes the pending download.
      const newPractice = action.info;
      if (state.modal.kind === "practice-setup-required" && state.flow.tag === "results") {
        return {
          practice: newPractice,
          modal: { kind: "closed" },
          flow: {
            tag: "generating-pdf",
            values: state.flow.values,
            input: state.flow.input,
            results: state.flow.results,
          },
        };
      }
      return { ...state, practice: newPractice, modal: { kind: "closed" } };
    }

    case "practice-cancelled": {
      // Soft-gate cancel: user didn't want to download after all.
      // Edit cancel: user changed their mind. Either way: close modal,
      // leave practice untouched, leave flow untouched.
      return { ...state, modal: { kind: "closed" } };
    }

    case "reset-form": {
      return { ...state, flow: { tag: "ready" } };
    }
  }
}
